/* Theme custom file */
const OPERATION_REMINDER_STYLES_ID = 'operation-reminder-styles';
// App Proxy routes of web-bff (/apps/web/* -> /v1/proxy/*). core-api behind
// them records the reminder and subscribes the email to the newsletter list,
// so the browser never calls Klaviyo for a sign-up. The reminder mails are
// sent later from the recorded reminders.
const REMINDER_ENDPOINT = '/apps/web/operation-reminders';
const VISITOR_REMINDER_ENDPOINT = '/apps/web/visitor-operation-reminders';

class OperationReminder extends HTMLElement {
  static STEPS = { IDLE: 'idle', FORM: 'form', SUBMITTING: 'submitting', SUCCESS: 'success', ERROR: 'error' };

  static STORAGE_KEY = 'operation-reminders';

  /** @type {Promise<Set<string>> | null} */
  static #remindersPromise = null;

  connectedCallback() {
    OperationReminder.injectStyles();

    // data-operation-id is the core-api id of an upcoming sale; without it the
    // sign-up is a newsletter subscribe only (ended and empty collections).
    // The id keys the subscription state kept in this browser: the handle is
    // shared by every sale of a brand, so a reminder on one sale would show as
    // set on the brand's next one. Forms without an id keep the handle.
    this.operationId = this.dataset.operationId || '';
    this.operationKey = this.operationId || this.getAttribute('operation-handle') || '';
    this.endDate = (this.dataset.operationEndDate || '').slice(0, 10);
    this.customerEmail = this.dataset.customerEmail || '';

    // Read identity context from header-component (present on all pages)
    const header = document.getElementById('header-component');
    this.currentLoggedIn = header?.hasAttribute('data-customer-logged-in') ?? false;
    this.currentEmail = header?.dataset.customerEmail || '';

    // A signed-in customer's reminder on a sale lives in core-api, shared with
    // the app bell: read it there. Everything else keeps the browser state.
    this.readsServerState = this.isSignedIn() && Boolean(this.operationId);
    if (!this.readsServerState && this.isAlreadySubscribed()) {
      this.dataset.step = OperationReminder.STEPS.SUCCESS;
    } else {
      this.dataset.step = OperationReminder.STEPS.IDLE;
    }
    if (this.readsServerState) this.#showServerState();

    this.alertBtn = this.querySelector('[data-operation-reminder-trigger]');
    this.formEl = this.querySelector('.operation-reminder__form');
    this.emailInput = this.querySelector('.operation-reminder__input');
    this.submitBtn = this.querySelector('.operation-reminder__submit');
    this.successEl = this.querySelector('.operation-reminder__success');
    this.errorEl = this.querySelector('.operation-reminder__error');
    this.bindEvents();

    if (this.customerEmail) {
      this.emailInput.value = this.customerEmail;
    }
  }

  disconnectedCallback() {
    this.alertBtn?.removeEventListener('click', this.handleTriggerClick);
    this.submitBtn?.removeEventListener('click', this.handleSubmit);
    clearTimeout(this.errorTimeout);
  }

  bindEvents() {
    this.handleTriggerClick = () => {
      if (this.customerEmail) {
        this.submit();
      } else {
        this.goTo(OperationReminder.STEPS.FORM);
      }
    };
    this.handleSubmit = () => this.submit();

    this.alertBtn?.addEventListener('click', this.handleTriggerClick);
    this.submitBtn.addEventListener('click', this.handleSubmit);

    this.emailInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.submit();
    });
  }

  goTo(step) {
    this.dataset.step = step;

    if (step === OperationReminder.STEPS.FORM) {
      requestAnimationFrame(() => this.emailInput.focus());
    }

    if (step === OperationReminder.STEPS.ERROR) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = setTimeout(() => this.goTo(OperationReminder.STEPS.FORM), 2000);
    }
  }

  async submit() {
    // Enter and the button can both fire: one sign-up per submit, since each
    // request counts toward core-api's hourly limit.
    if (this.dataset.step === OperationReminder.STEPS.SUBMITTING) return;

    const email = this.customerEmail || this.emailInput.value.trim();

    if (!this.customerEmail) {
      if (!this.emailInput.reportValidity()) return;
      if (!email) return;
    }

    this.goTo(OperationReminder.STEPS.SUBMITTING);

    try {
      await this.#postReminder(email);
      if (this.readsServerState) {
        (await OperationReminder.signedInReminders()).add(this.operationId);
      }
      this.saveSubscription();
      this.dispatchAnalytics(email);
      this.goTo(OperationReminder.STEPS.SUCCESS);
    } catch {
      this.goTo(OperationReminder.STEPS.ERROR);
    }
  }

  isSignedIn() {
    return this.currentLoggedIn || Boolean(this.customerEmail);
  }

  /**
   * Records the reminder through web-bff. A signed-in customer is taken from
   * the Shopify-signed App Proxy request; the email is only a fallback for an
   * account core-api has not recorded yet. A visitor is the typed email.
   */
  async #postReminder(email) {
    const body = { email };
    if (this.operationId) body.operationId = this.operationId;

    const response = await fetch(this.isSignedIn() ? REMINDER_ENDPOINT : VISITOR_REMINDER_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error(`Reminder sign-up failed (${response.status})`);
  }

  /** Shows the subscribed state when core-api holds this customer's reminder. */
  async #showServerState() {
    const reminders = await OperationReminder.signedInReminders();
    if (reminders.has(this.operationId) && this.dataset.step === OperationReminder.STEPS.IDLE) {
      this.dataset.step = OperationReminder.STEPS.SUCCESS;
    }
  }

  /**
   * The sales the signed-in customer holds a reminder on, set on the site or
   * in the app. One request per page, shared by every reminder on it. A failed
   * read shows no reminder: the customer can set it again, which is harmless.
   *
   * @returns {Promise<Set<string>>}
   */
  static signedInReminders() {
    OperationReminder.#remindersPromise ??= OperationReminder.#fetchSignedInReminders();
    return OperationReminder.#remindersPromise;
  }

  /** @returns {Promise<Set<string>>} */
  static async #fetchSignedInReminders() {
    try {
      const response = await fetch(REMINDER_ENDPOINT, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Reminder list failed (${response.status})`);
      // web-bff wraps every answer in { statusCode, data }.
      const payload = await response.json();
      const operationIds = payload?.data?.operationIds;
      return new Set(Array.isArray(operationIds) ? operationIds : []);
    } catch (error) {
      console.error('[operation-reminder]', error);
      return new Set();
    }
  }

  /**
   * Check if this operation was already subscribed to, factoring in identity.
   *
   * Storage format: { [operationId or operationHandle]: { email: string, loggedIn: boolean, endDate: string } }
   *
   * Rules:
   * - Logged in now: show success if stored entry email matches current account email
   * - Visitor now: show success if stored entry was made as visitor (loggedIn: false),
   *   OR if stored email matches current visitor context (no customer email to compare)
   */
  isAlreadySubscribed() {
    const entry = this.#loadStore()[this.operationKey];
    return entry ? this.#checkEntry(entry) : false;
  }

  /**
   * Load the subscription store, resetting legacy formats and pruning entries
   * for operations whose end date has passed. Persists the prune if it removed
   * anything so stale handles do not accumulate.
   *
   * @returns {Record<string, { email: string, loggedIn: boolean, endDate?: string }>}
   */
  #loadStore() {
    let stored;
    try {
      stored = JSON.parse(localStorage.getItem(OperationReminder.STORAGE_KEY) || '{}');
    } catch {
      return {};
    }

    // Legacy array format held bare list IDs with no identity or date context
    // and cannot be reconciled to operation handles, so start fresh.
    if (stored === null || typeof stored !== 'object' || Array.isArray(stored)) {
      return {};
    }

    const today = new Date().toISOString().slice(0, 10);
    let changed = false;
    for (const key of Object.keys(stored)) {
      const endDate = stored[key]?.endDate;
      if (endDate && endDate < today) {
        delete stored[key];
        changed = true;
      }
    }

    if (changed) {
      try {
        localStorage.setItem(OperationReminder.STORAGE_KEY, JSON.stringify(stored));
      } catch { /* localStorage unavailable */ }
    }

    return stored;
  }

  /** @param {{ email: string, loggedIn: boolean }} entry */
  #checkEntry(entry) {
    // If emails match, always show success regardless of login state transition
    if (entry.email && this.currentEmail && entry.email === this.currentEmail) {
      return true;
    }

    if (this.currentLoggedIn) {
      // Logged in, emails don't match: not this account's subscription
      return false;
    }

    // Visitor: show success only if entry was also made as visitor
    return !entry.loggedIn;
  }

  saveSubscription() {
    try {
      const stored = this.#loadStore();
      const email = this.currentLoggedIn ? this.currentEmail : (this.emailInput?.value.trim() || '');
      stored[this.operationKey] = { email, loggedIn: this.currentLoggedIn, endDate: this.endDate };
      localStorage.setItem(OperationReminder.STORAGE_KEY, JSON.stringify(stored));
    } catch { /* localStorage unavailable */ }
  }

  dispatchAnalytics(email) {
    document.dispatchEvent(new CustomEvent('subscribeToSale', {
      detail: {
        brand_name: this.dataset.operationName || '',
        start_date: this.dataset.operationStartDate || '',
        end_date: this.dataset.operationEndDate || '',
      },
    }));

    if (typeof klaviyo !== 'undefined') {
      klaviyo.identify({ email });
      // Same metric and properties as the app's physical-sale reminder, so CRM
      // runs one flow for both. Other sales send no event.
      if (this.dataset.saleType === 'physical_sale' && typeof klaviyo.track === 'function') {
        klaviyo.track('subscribed_physical_sale', this.#physicalSaleProperties());
      }
    }
  }

  #physicalSaleProperties() {
    const properties = { sale_type: 'physical_sale' };
    if (this.operationId) properties.internal_id = this.operationId;
    if (this.dataset.operationName) properties.operation_name = this.dataset.operationName;
    for (const [key, value] of [
      ['start_date', this.dataset.operationStartDate],
      ['end_date', this.dataset.operationEndDate],
    ]) {
      const date = new Date(value || '');
      if (!Number.isNaN(date.getTime())) properties[key] = date.toISOString();
    }
    if (this.dataset.saleUrl) properties.sale_url = this.dataset.saleUrl;
    return properties;
  }

  static injectStyles() {
    if (document.getElementById(OPERATION_REMINDER_STYLES_ID)) return;

    const style = document.createElement('style');
    style.id = OPERATION_REMINDER_STYLES_ID;
    style.textContent = `
      operation-reminder {
        display: block;
      }

      /* Step visibility */
      operation-reminder[data-step="idle"] .operation-reminder__form,
      operation-reminder[data-step="idle"] .operation-reminder__success,
      operation-reminder[data-step="idle"] .operation-reminder__error,
      operation-reminder[data-step="form"] [data-operation-reminder-trigger],
      operation-reminder[data-step="form"] .operation-reminder__success,
      operation-reminder[data-step="form"] .operation-reminder__error,
      operation-reminder[data-step="submitting"] .operation-reminder__form,
      operation-reminder[data-step="submitting"] .operation-reminder__success,
      operation-reminder[data-step="submitting"] .operation-reminder__error,
      operation-reminder[data-step="success"] [data-operation-reminder-trigger],
      operation-reminder[data-step="success"] .operation-reminder__form,
      operation-reminder[data-step="success"] .operation-reminder__error,
      operation-reminder[data-step="error"] [data-operation-reminder-trigger],
      operation-reminder[data-step="error"] .operation-reminder__form,
      operation-reminder[data-step="error"] .operation-reminder__success {
        display: none !important;
      }

      /* Form container — glass pill */
      .operation-reminder__form {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        padding: 12px;
        height: 40px;
        background: rgba(201, 201, 201, 0.26);
        backdrop-filter: blur(22px);
        border-radius: 6px;
        box-sizing: border-box;
      }

      /* Email input — transparent, inherits pill styling */
      .operation-reminder__input {
        flex: 1;
        min-width: 0;
        border: none !important;
        background: transparent !important;
        outline: none;
        padding: 0;
        margin: 0;
        font-family: 'DM Sans', var(--font-body--family);
        font-weight: 600;
        font-size: 10px;
        line-height: 120%;
        letter-spacing: 0.09em;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.7);
      }

      .operation-reminder__input::placeholder {
        color: rgba(255, 255, 255, 0.7);
      }

      /* Submit button */
      .operation-reminder__submit {
        flex-shrink: 0;
        border: none;
        background: transparent;
        padding: 0;
        margin: 0;
        cursor: pointer;
        font-family: 'DM Sans', var(--font-body--family);
        font-weight: 600;
        font-size: 10px;
        line-height: 120%;
        letter-spacing: 0.09em;
        text-transform: uppercase;
        color: #fff;
        min-height: 0;
        min-width: 0;
      }

      /* Submitting state — show trigger as spinner */
      operation-reminder[data-step="submitting"] [data-operation-reminder-trigger] {
        pointer-events: none;
      }

      operation-reminder[data-step="submitting"] [data-operation-reminder-trigger] svg {
        display: none;
      }

      operation-reminder[data-step="submitting"] [data-operation-reminder-trigger]::after {
        content: '';
        width: 16px;
        height: 16px;
        border: 2px solid rgb(255 255 255 / 0.3);
        border-top-color: #fff;
        border-radius: 50%;
        animation: operation-reminder-spin 0.6s linear infinite;
      }

      @keyframes operation-reminder-spin {
        to { rotate: 360deg; }
      }

      /* Error container — same glass pill with shake */
      .operation-reminder__error {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 12px;
        height: 40px;
        background: rgba(201, 201, 201, 0.26);
        backdrop-filter: blur(22px);
        border-radius: 6px;
        box-sizing: border-box;
        font-family: 'DM Sans', var(--font-body--family);
        font-weight: 600;
        font-size: 10px;
        line-height: 120%;
        letter-spacing: 0.09em;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.7);
        animation: operation-reminder-shake 0.3s ease-out;
      }

      @keyframes operation-reminder-shake {
        0%, 100% { transform: translateX(0); }
        20% { transform: translateX(-4px); }
        40% { transform: translateX(4px); }
        60% { transform: translateX(-2px); }
        80% { transform: translateX(2px); }
      }

      /* Success container — same glass pill */
      .operation-reminder__success {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 12px;
        height: 40px;
        background: rgba(201, 201, 201, 0.26);
        backdrop-filter: blur(22px);
        border-radius: 6px;
        box-sizing: border-box;
        font-family: 'DM Sans', var(--font-body--family);
        font-weight: 600;
        font-size: 10px;
        line-height: 120%;
        letter-spacing: 0.09em;
        text-transform: uppercase;
        color: #fff;
      }
    `;

    document.head.appendChild(style);
  }
}

customElements.define('operation-reminder', OperationReminder);
